import type { ReactNode } from "react";
import type { TrainingSessionListView } from "@/api";
import { truncationMarkerProps } from "@/components/table/truncationCell";
import {
  openTrainingSessionWindow,
  type TrainingSessionWindowFocus,
} from "./trainingSessionWindow";
import styles from "./TrainingSessionRefLink.module.css";

export type TrainingSessionRefLinkProps = {
  sessionId: string | null;
  displayName: string;
  // 여는 창의 제목 seed. 셀이 세션 이름이 아닌 값을 표시하는 호출부만 따로 전달한다. 전달하지
  // 않으면 displayName 을 세션 이름으로 쓴다. 그러면 상세 응답이 올 때까지 창 제목이 틀린다.
  sessionName?: string;
  // 여는 창이 먼저 표시할 위치. Policy 이름 셀은 체크포인트 탭의 그 정책 행을 가리킨다. 전달하지
  // 않으면 로그 탭이다.
  focus?: TrainingSessionWindowFocus;
  className?: string;
};

export function TrainingSessionRefLink({
  sessionId,
  displayName,
  sessionName,
  focus,
  className,
}: TrainingSessionRefLinkProps): ReactNode {
  // 잘리는 요소와 마커 요소가 같아야 한다. 래퍼에 달면 안쪽 요소가 먼저 잘려 잘림을 감지하지 못한다.
  const truncation = truncationMarkerProps;
  const valueClassName = className === undefined ? styles.value : `${styles.value} ${className}`;

  if (sessionId === null) {
    return (
      <span className={valueClassName} {...truncation}>
        {displayName}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={`${valueClassName} ${styles.link}`}
      onClick={() =>
        openTrainingSessionWindow(sessionReference(sessionId, sessionName ?? displayName), {
          focus,
        })
      }
      {...truncation}
    >
      {displayName}
    </button>
  );
}

// 세션 참조의 최소 형태. 목록 응답 없이 id 와 이름만 아는 호출부가 창을 열 때 전달한다. 나머지
// 필드는 창이 상세를 받아 채우므로 미상으로 둔다. 이 함수를 창 모듈이 아니라 여기 두는 이유가
// 있다. 테스트들이 그 모듈을 직접 만든 부분 mock 으로 감싼다. 그래서 export 가 늘면 그 mock 들이
// 알림 없이 실패한다.
export function sessionReference(sessionId: string, displayName: string): TrainingSessionListView {
  return {
    id: sessionId,
    status: "unknown",
    display_name: displayName,
    experiment_id: null,
    experiment_name: null,
    workspace_id: null,
    workspace_name: null,
    created_at: null,
    started_at: null,
    finished_at: null,
    progress: null,
    schedule_origin: null,
    note: null,
    registry_user: null,
    dataset: null,
    dataset_count: 0,
    leading_datasets: [],
    init_strategy: null,
  };
}
